/**
 * A11y Toggles
 * An accessible replacement to the checkbox-hack for toggling sections.
 *
 * By: https://hugogiraudel.com/
 * MIT License: https://github.com/edenspiekermann/a11y-toggle/blob/master/LICENSE
 */
export default () => {
    const windowWidth = window.innerWidth;
    // eslint-disable-next-line no-underscore-dangle
    const _hook = (selector, base) => {
        const context = base || document;
        let elements;

        if (!selector || selector === '*') {
            elements = context.querySelectorAll('[data-hook]');
        } else {
            elements = context.querySelectorAll(`[data-hook~="${selector}"]`);
        }

        return (elements.length === 1) ? elements[0] : elements;
    };

    const distinct = (value, index, self) => self.indexOf(value) === index;

    let atResizeTimeout;
    let internalId = 0;
    let togglesMap = {};
    const targetsMap = {};

    function $(selector, context) {
        return Array.prototype.slice.call(
            (context || document).querySelectorAll(selector),
        );
    }

    function getClosestToggle(element) {
        if (element.closest) {
            return element.closest('[data-a11y-toggle]');
        }

        while (element) {
            if (element.nodeType === 1 && element.hasAttribute('data-a11y-toggle')) {
                return element;
            }

            // eslint-disable-next-line no-param-reassign
            element = element.parentNode;
        }

        return null;
    }

    function handleToggle(toggle) {
        const target = toggle && targetsMap[toggle.getAttribute('aria-controls')];

        if (!target) {
            return false;
        }

        const toggles = togglesMap[`#${target.id}`];
        const isExpanded = target.getAttribute('aria-hidden') === 'false';

        target.setAttribute('aria-hidden', isExpanded);
        toggles.forEach((item) => {
            item.setAttribute('aria-expanded', !isExpanded);
        });
    }

    const initA11yToggle = (context) => {
        togglesMap = $('[data-a11y-toggle]', context).reduce((acc, toggle) => {
            const selector = `#${toggle.getAttribute('data-a11y-toggle')}`;

            acc[selector] = acc[selector] || [];
            acc[selector].push(toggle);
            return acc;
        }, togglesMap);

        const targets = Object.keys(togglesMap);

        if (targets.length) {
            $(targets).forEach((target) => {
                const toggles = togglesMap[`#${target.id}`];
                const isExpanded = target.hasAttribute('data-a11y-toggle-open');
                const labelledby = [];

                if (toggles[0].offsetWidth > 0 && toggles[0].offsetHeight > 0) {
                    toggles.forEach((toggle) => {
                        if (!toggle.id) {
                            toggle.setAttribute('id', `a11y-toggle-${internalId++}`);
                        }

                        toggle.setAttribute('aria-controls', target.id);
                        toggle.setAttribute('aria-expanded', isExpanded);
                        labelledby.push(toggle.id);
                    });
                    const distinctLabel = labelledby.filter(distinct);

                    target.setAttribute('aria-hidden', !isExpanded);
                    if (!target.hasAttribute('aria-labelledby')) {
                        target.setAttribute('aria-labelledby', distinctLabel.join(' '));
                    }
                    target.setAttribute('role', 'region');
                } else {
                    toggles.forEach((toggle) => {
                        toggle.removeAttribute('id');
                        toggle.removeAttribute('aria-controls');
                        toggle.removeAttribute('aria-expanded');
                    });

                    target.removeAttribute('aria-hidden');
                    target.removeAttribute('aria-labelledby');
                    target.removeAttribute('role');
                }

                targetsMap[target.id] = target;
            });
        }
    };

    const destroyA11yToggle = () => {
        const targets = Object.keys(togglesMap);

        if (targets.length) {
            $(targets).forEach((target) => {
                const toggles = togglesMap[`#${target.id}`];

                toggles.forEach((toggle) => {
                    toggle.removeAttribute('id');
                    toggle.removeAttribute('aria-controls');
                    toggle.removeAttribute('aria-expanded');
                });

                target.removeAttribute('aria-hidden');
                target.removeAttribute('aria-labelledby');
                target.removeAttribute('role');

                targetsMap[target.id] = target;
            });
        }
    };

    const closeA11yToggle = (trigger) => {
        if (trigger) {
            const thisToggle = document.querySelector(`#${trigger.getAttribute('data-a11y-toggle')}`);

            document.addEventListener('mousedown', (event) => {
                if (thisToggle.getAttribute('aria-hidden') === 'false') {
                    if (!thisToggle.contains(event.target) && event.target !== trigger) {
                        trigger.click();
                        event.preventDefault();
                    }
                }
            }, false);
        }
    };

    window.addEventListener('resize', () => {
        if (atResizeTimeout) {
            window.cancelAnimationFrame(atResizeTimeout);
        }

        if (windowWidth !== window.innerWidth) {
            atResizeTimeout = window.requestAnimationFrame(() => {
                initA11yToggle();
            });
        }
    }, false);

    document.addEventListener('click', (event) => {
        const toggle = getClosestToggle(event.target);

        handleToggle(toggle);
    });

    document.addEventListener('keyup', (keyupEvent) => {
        if (keyupEvent.key === 'Enter' || keyupEvent.key === ' ') {
            const toggle = getClosestToggle(keyupEvent.target);

            if (toggle && toggle.getAttribute('role') === 'button') {
                handleToggle(toggle);
            }
        }
    });

    if (window) {
        window.a11yToggle = initA11yToggle;
        window.a11yToggleDestroy = destroyA11yToggle;
        window.a11yToggleClose = closeA11yToggle;
    }

    if (_hook('global-account') && _hook('global-account').length !== 0) {
        window.a11yToggleClose(_hook('global-account')); // Close the global account box when clicking on a different target.
    }

    initA11yToggle();
};
